/**
 * ============================================================================
 * Iron Meridian - useConnectionStatus Hook
 * ============================================================================
 * 
 * React hook for subscribing to connection status changes.
 * Returns current status and updates when status changes.
 * 
 * USAGE:
 * const { status, isConnected, isOnline, isLocal, isOffline } = useConnectionStatus()
 * 
 * ============================================================================
 */

import { useState, useEffect } from 'react'
import { connectionStatus, CONNECTION_STATUS } from '../services/connectionStatus.js'

/**
 * Hook for reactive connection status updates
 * @returns {Object} Connection status object
 */
export function useConnectionStatus() {
  const [status, setStatus] = useState(() => connectionStatus.getStatus())

  useEffect(() => {
    // Subscribe to status changes
    const unsubscribe = connectionStatus.subscribe((newStatus) => {
      setStatus(newStatus)
    })

    // Start periodic checks (every 30 seconds)
    connectionStatus.startPeriodicCheck(30000)

    // Get initial status
    setStatus(connectionStatus.getStatus())

    return () => {
      unsubscribe()
      // Don't stop periodic check on unmount - other components may need it
    }
  }, [])

  return {
    status,
    isConnected: status === CONNECTION_STATUS.CONNECTED,
    isOnline: status === CONNECTION_STATUS.ONLINE,
    isLocal: status === CONNECTION_STATUS.LOCAL,
    isOffline: status === CONNECTION_STATUS.OFFLINE,
    // Helper for checking if persistence is working (either local or connected)
    isPersisting: status === CONNECTION_STATUS.CONNECTED || status === CONNECTION_STATUS.LOCAL
  }
}

export default useConnectionStatus
